/**
 * V2 Products Search
 * The page lists the Online Store catalog. A query loads that same catalog
 * (preset chips still add filter.p.tag, OR) and keeps a card when every query
 * word is in its data-v2-products-search-text value. That value includes the
 * translated custom.search_terms metafield. Shopify /search does not.
 * Chips with an empty search load the all-products collection with that filter.
 * Clearing the query and the chips loads the catalog again.
 * Reset all in the filters drawer clears the query, every tag, and pagination,
 * then drops those params from the page URL and loads the full catalog.
 */
(function () {
  const SECTION_SELECTOR = '[data-v2-products-search]';
  const CATALOG_SECTION_FILE = 'v2-products-search-catalog';
  const TAG_PARAM = 'filter.p.tag';

  function pageNumber(url) {
    const value = new URL(url, window.location.origin).searchParams.get('page');
    return value || '';
  }

  function tagsFromParams(params) {
    const tags = [];
    params.getAll(TAG_PARAM).forEach(function (value) {
      value.split(',').forEach(function (part) {
        const tag = part.trim();
        if (tag && tags.indexOf(tag) === -1) tags.push(tag);
      });
    });
    return tags;
  }

  function setTagParam(url, tags) {
    url.searchParams.delete(TAG_PARAM);
    tags.forEach(function (tag) {
      url.searchParams.append(TAG_PARAM, tag);
    });
    return url;
  }

  function isSearchParam(key) {
    return (
      key === 'q' ||
      key === 'page' ||
      key === 'type' ||
      key === 'section_id' ||
      key.indexOf('filter.') === 0 ||
      key.indexOf('options[') === 0
    );
  }

  function dropSearchParams(url) {
    const keys = [];
    url.searchParams.forEach(function (_value, key) {
      if (isSearchParam(key)) keys.push(key);
    });
    keys.forEach(function (key) {
      url.searchParams.delete(key);
    });
    return url;
  }

  function visibleUrl(query, page, tags) {
    const url = dropSearchParams(new URL(window.location.href));
    if (query) url.searchParams.set('q', query);
    if (page) url.searchParams.set('page', page);
    setTagParam(url, tags);
    return url.pathname + url.search;
  }

  function init(root) {
    if (!root || root.dataset.v2ProductsSearchReady === 'true') return;
    root.dataset.v2ProductsSearchReady = 'true';

    const form = root.querySelector('form');
    const input = root.querySelector('[data-v2-products-search-input]');
    const grid = root.querySelector('[data-v2-products-search-grid]');
    const empty = root.querySelector('[data-v2-products-search-empty]');
    const status = root.querySelector('[data-v2-products-search-status]');
    const results = root.querySelector('[data-v2-products-search-results]');
    const presets = root.querySelectorAll('[data-v2-products-search-preset]');
    if (!form || !input || !grid || !results) return;

    let controller = null;

    function setStatus(message) {
      if (!status) return;
      if (message) {
        status.textContent = message;
        status.hidden = false;
      } else {
        status.textContent = '';
        status.hidden = true;
      }
    }

    function setBusy(on) {
      grid.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    function showEmpty(count) {
      if (!empty) return;
      empty.hidden = count > 0;
    }

    function normalizeQuery(text) {
      let value = String(text || '').toLowerCase();
      [',', '.', ';', ':', '!', '?', '(', ')', '[', ']', '{', '}', '/', '|', '"', "'", '+', '=', '*', '&', '#', '@', '~', '%', '$', '<', '>', '–', '—', '-', '_'].forEach(function (mark) {
        value = value.split(mark).join(' ');
      });
      return value.replace(/\s+/g, ' ').trim();
    }

    function queryMatches(haystack, query) {
      const terms = normalizeQuery(query).split(' ').filter(Boolean);
      if (!terms.length) return true;
      const padded = ' ' + haystack + ' ';
      for (let index = 0; index < terms.length; index += 1) {
        const needle = index === terms.length - 1 ? ' ' + terms[index] : ' ' + terms[index] + ' ';
        if (padded.indexOf(needle) === -1) return false;
      }
      return true;
    }

    function keepMatches(query) {
      grid.querySelectorAll('[data-v2-products-search-card]').forEach(function (card) {
        const haystack = card.getAttribute('data-v2-products-search-text') || '';
        if (!queryMatches(haystack, query)) card.remove();
      });
    }

    function applyHtml(html, query) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-v2-products-search-grid]');
      if (!nextGrid) {
        setStatus(root.getAttribute('data-error-message') || '');
        return false;
      }
      grid.innerHTML = nextGrid.innerHTML;
      if (query) keepMatches(query);
      showEmpty(grid.querySelectorAll('[data-v2-products-search-card]').length);
      setStatus('');
      return true;
    }

    function fetchSection(url, query) {
      if (controller) controller.abort();
      const current = new AbortController();
      controller = current;
      setBusy(true);
      setStatus(root.getAttribute('data-searching-message') || '');

      return fetch(url.toString(), {
        signal: current.signal,
        headers: { Accept: 'text/html' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error('search failed');
          return response.text();
        })
        .then(function (html) {
          if (controller !== current) return;
          applyHtml(html, query || '');
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (controller !== current) return;
          setStatus(root.getAttribute('data-error-message') || '');
        })
        .finally(function () {
          if (controller !== current) return;
          setBusy(false);
        });
    }

    const drawer = root.querySelector('[data-v2-drawer]');
    const drawerOptions = root.querySelectorAll('[data-v2-products-search-drawer-option]');
    const drawerApply = root.querySelector('[data-v2-products-search-drawer-apply]');
    const drawerReset = root.querySelector('[data-v2-products-search-drawer-reset]');
    const drawerSelectAll = root.querySelector('[data-v2-products-search-drawer-select-all]');
    let applied = [];
    let draft = [];

    function copyTags(tags) {
      return tags.slice();
    }

    function toggleTag(tags, tag) {
      const next = copyTags(tags);
      const index = next.indexOf(tag);
      if (index === -1) next.push(tag);
      else next.splice(index, 1);
      return next;
    }

    function drawerOpen() {
      return !!(drawer && drawer.classList.contains('is-open'));
    }

    function drawerTags() {
      const tags = [];
      drawerOptions.forEach(function (option) {
        const tag = option.getAttribute('data-tag');
        if (tag && tags.indexOf(tag) === -1) tags.push(tag);
      });
      return tags;
    }

    function allDrawerTagsSelected(tags) {
      const required = drawerTags();
      if (!required.length) return true;
      return required.every(function (tag) {
        return tags.indexOf(tag) !== -1;
      });
    }

    function syncControls(tags) {
      presets.forEach(function (preset) {
        setPressed(preset, tags.indexOf(preset.getAttribute('data-tag')) !== -1);
      });
      drawerOptions.forEach(function (option) {
        option.checked = tags.indexOf(option.getAttribute('data-tag')) !== -1;
      });
      if (drawerSelectAll) drawerSelectAll.hidden = allDrawerTagsSelected(tags);
    }

    function selectedTags() {
      return copyTags(applied);
    }

    function withPreview(url) {
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      return url;
    }

    function matchUrl(page, tags) {
      const url = new URL(root.getAttribute('data-catalog-url') || '/collections/all', window.location.origin);
      url.searchParams.set('section_id', CATALOG_SECTION_FILE);
      if (page) url.searchParams.set('page', page);
      withPreview(url);
      setTagParam(url, tags);
      return url;
    }

    function catalogUrl(page, tags) {
      if (tags.length) {
        const url = new URL(root.getAttribute('data-catalog-url') || '/collections/all', window.location.origin);
        url.searchParams.set('section_id', CATALOG_SECTION_FILE);
        if (page) url.searchParams.set('page', page);
        withPreview(url);
        setTagParam(url, tags);
        return url;
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('q');
      url.searchParams.delete('type');
      url.searchParams.delete('options[prefix]');
      url.searchParams.delete(TAG_PARAM);
      url.searchParams.set('section_id', root.getAttribute('data-section-id'));
      if (page) url.searchParams.set('page', page);
      else url.searchParams.delete('page');
      return url;
    }

    function run(query, page) {
      const trimmed = (query || '').trim();
      const tags = selectedTags();
      history.replaceState({}, '', visibleUrl(trimmed, page, tags));
      if (!trimmed) {
        input.value = '';
        return fetchSection(catalogUrl(page, tags), '');
      }
      input.value = trimmed;
      return fetchSection(matchUrl(page, tags), trimmed);
    }

    function setPressed(preset, pressed) {
      preset.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }

    function setDrawerExpanded(open) {
      if (!filterButton) return;
      filterButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    const filterButton = root.querySelector('[data-v2-products-search-filter]');
    if (filterButton && drawer && window.V2Drawer) {
      filterButton.addEventListener('click', function () {
        draft = copyTags(applied);
        syncControls(draft);
        setDrawerExpanded(true);
        window.V2Drawer.open(drawer);
      });
    }

    drawerOptions.forEach(function (option) {
      option.addEventListener('change', function () {
        draft = toggleTag(draft, option.getAttribute('data-tag'));
        syncControls(draft);
      });
    });

    if (drawerSelectAll) {
      drawerSelectAll.addEventListener('click', function () {
        const next = copyTags(draft);
        drawerTags().forEach(function (tag) {
          if (next.indexOf(tag) === -1) next.push(tag);
        });
        draft = next;
        syncControls(draft);
      });
    }

    if (drawerReset) {
      drawerReset.addEventListener('click', function () {
        applied = [];
        draft = [];
        syncControls(applied);
        run('', '');
      });
    }

    if (drawerApply && drawer && window.V2Drawer) {
      drawerApply.addEventListener('click', function () {
        applied = copyTags(draft);
        syncControls(applied);
        run(input.value, '');
        setDrawerExpanded(false);
        window.V2Drawer.close(drawer);
      });
    }

    if (drawer) {
      drawer.addEventListener('v2-drawer:dismiss', function () {
        draft = copyTags(applied);
        syncControls(applied);
        setDrawerExpanded(false);
        if (window.V2Drawer) window.V2Drawer.close(drawer);
      });
    }

    presets.forEach(function (preset) {
      preset.addEventListener('click', function () {
        const tag = preset.getAttribute('data-tag');
        if (drawerOpen()) {
          draft = toggleTag(draft, tag);
          syncControls(draft);
          return;
        }
        applied = toggleTag(applied, tag);
        syncControls(applied);
        run(input.value, '');
      });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      run(input.value, '');
    });

    results.addEventListener('click', function (event) {
      const link = event.target.closest('[data-v2-products-search-page]');
      if (!link || !results.contains(link)) return;
      event.preventDefault();
      run(input.value, pageNumber(link.href));
    });

    const params = new URLSearchParams(window.location.search);
    const initialQuery = (params.get('q') || '').trim();
    applied = copyTags(tagsFromParams(params));
    draft = copyTags(applied);
    syncControls(applied);
    if (initialQuery || applied.length) {
      run(initialQuery, params.get('page') || '');
    }

    root._v2ProductsSearchDestroy = function () {
      if (controller) controller.abort();
      delete root.dataset.v2ProductsSearchReady;
    };
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(init);
  }

  initAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector(SECTION_SELECTOR) || event.target.closest(SECTION_SELECTOR);
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const root = event.target.querySelector(SECTION_SELECTOR) || event.target.closest(SECTION_SELECTOR);
    if (root && typeof root._v2ProductsSearchDestroy === 'function') {
      root._v2ProductsSearchDestroy();
    }
  });
})();
